import { BACKEND_URL, APP_VERSION } from "./env.js";

type Source =
  | "boundary"
  | "window_error"
  | "unhandled_rejection"
  | "manual";

export interface ClientErrorPayload {
  source: Source;
  message: string;
  stack?: string;
  route?: string;
}

/**
 * Pings the bot's `/api/client-error` so cluster logs surface every
 * Mini App crash, identified by the verified Telegram user.
 *
 * Auth: the bot endpoint requires a valid `X-Telegram-Init-Data`
 * header. If `window.Telegram.WebApp.initData` isn't available yet
 * (very early bootstrap crash, app opened outside Telegram), we skip
 * the network call. The error still shows in the on-screen boundary
 * and in the browser console — we just don't pollute the cluster log
 * with anonymous payloads.
 *
 * Best-effort delivery: never throws, never blocks. The caller is
 * already in a degraded state — adding a second crash on top would be
 * worse than missing one error.
 */
export function reportClientError(input: ClientErrorPayload): void {
  try {
    const initData = readInitData();
    if (!initData) return;

    const body = {
      ...input,
      build: APP_VERSION,
      ua: typeof navigator === "undefined" ? undefined : navigator.userAgent,
      client_time: new Date().toISOString(),
    };
    const url = `${BACKEND_URL}/api/client-error`;
    const json = JSON.stringify(body);

    if (typeof fetch !== "undefined") {
      void fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Telegram-Init-Data": initData,
        },
        body: json,
        // keepalive lets the request survive a page-teardown (refresh
        // after error). Capped at 64KB by the platform; our payload is
        // well under that.
        keepalive: true,
        credentials: "omit",
        mode: "cors",
      }).catch(() => {
        /* swallow — we're already in error path */
      });
      return;
    }
    // Fallback for non-fetch environments (test harnesses). sendBeacon
    // can't carry a custom header, so it's only used if fetch is gone
    // entirely — at which point we accept that the bot will reject the
    // payload as unauthenticated. Not great; but we don't expect to hit
    // this branch in any real Telegram WebView.
    const beacon = (navigator as unknown as { sendBeacon?: typeof navigator.sendBeacon })
      .sendBeacon;
    if (beacon) {
      const blob = new Blob([json], { type: "application/json" });
      beacon.call(navigator, url, blob);
    }
  } catch {
    // Reporter must not itself throw.
  }
}

/**
 * Best-effort read of the current Telegram WebApp init_data string.
 * Returns null if Telegram's polyfill hasn't injected the WebApp yet,
 * or if we're running outside a Telegram client entirely.
 */
function readInitData(): string | null {
  try {
    const wa = (
      window as unknown as {
        Telegram?: { WebApp?: { initData?: string } };
      }
    ).Telegram?.WebApp;
    const data = wa?.initData;
    return data && data.length > 0 ? data : null;
  } catch {
    return null;
  }
}

/**
 * Install once at app boot. Captures uncaught exceptions and unhandled
 * promise rejections, ships them to the bot, and lets the original
 * default handler still run (we don't preventDefault).
 */
export function installGlobalErrorReporter(): void {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __tgpeErrorReporter?: boolean }).__tgpeErrorReporter)
    return;
  (window as unknown as { __tgpeErrorReporter?: boolean }).__tgpeErrorReporter =
    true;

  window.addEventListener("error", (ev) => {
    const err = ev.error as unknown;
    reportClientError({
      source: "window_error",
      message:
        err instanceof Error
          ? err.message
          : typeof ev.message === "string" && ev.message
            ? ev.message
            : "error",
      stack: err instanceof Error ? err.stack : undefined,
      route: window.location.pathname,
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason as unknown;
    reportClientError({
      source: "unhandled_rejection",
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : "unhandled rejection",
      stack: reason instanceof Error ? reason.stack : undefined,
      route: window.location.pathname,
    });
  });
}
